// One turn in the Analyst thread.
//
// Sketch-faithful layout: the user's question is a right-aligned
// bubble (panel-2 surface) with the same solid primary avatar as the
// global header; the assistant's answer renders flush on the canvas -
// tool receipts, text, charts and tables in chronological order,
// followed by a quiet action row (Copy / Try again / "N queries" /
// usage). The audit trail of executed data queries opens from the
// "N queries" toggle in that row; UI-presentation tools never appear
// in it.

import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Stack, Typography, Chip, Tooltip, Avatar,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import {
  Storage as StorageIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ContentCopy as ContentCopyIcon,
  Replay as ReplayIcon,
  Check as CheckIcon,
  DescriptionOutlined as FileIcon,
} from '@mui/icons-material';

import AskDataTable from './AskDataTable';
import AskDataChart from './AskDataChart';
import MarkdownBody from './MarkdownBody';
import ToolStep from './ToolStep';
import InteractiveBlock, { INTERACTIVE_KINDS } from './InteractiveBlocks';
import ArtifactCard from './ArtifactCard';
import CodeRunBlock from './CodeRunBlock';
import { surfacesFor, initialsOf } from './uiTokens';


const formatCredits = (n) => {
  const v = Number(n || 0);
  if (!v) return '0';
  if (v < 0.01) return v.toFixed(4);
  if (v < 1)    return v.toFixed(2);
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatBytes = (n) => {
  const v = Number(n || 0);
  if (!v) return '';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
};


// File chips shown on the user bubble for CSVs attached to the turn.
const UserAttachments = ({ attachments, hasText }) => {
  if (!Array.isArray(attachments) || !attachments.length) return null;
  return (
    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mb: hasText ? 1 : 0 }}>
      {attachments.map((a, i) => (
        <Stack
          key={i}
          direction="row" alignItems="center" spacing={0.6}
          sx={(theme) => {
            const s = surfacesFor(theme);
            return {
              px: 1.1, py: 0.5,
              borderRadius: '10px',
              border: `1px solid ${s.border2}`,
              bgcolor: s.panel,
              maxWidth: 240,
            };
          }}
        >
          <FileIcon
            sx={(theme) => ({
              fontSize: 14,
              color: theme.palette.mode === 'dark'
                ? (theme.brand?.lime || theme.palette.primary.main)
                : theme.palette.primary.main,
            })}
          />
          <Typography noWrap sx={(t) => ({ fontSize: 11.5, fontWeight: 600, color: surfacesFor(t).text })}>
            {a.filename || 'file.csv'}
          </Typography>
          {a.size_bytes ? (
            <Typography sx={(t) => ({ fontSize: 10, color: surfacesFor(t).faint, flexShrink: 0 })}>
              {formatBytes(a.size_bytes)}
            </Typography>
          ) : null}
        </Stack>
      ))}
    </Stack>
  );
};


// Tools that present UI rather than touch data. They never belong in the
// "queries executed" audit trail - the rendered block/chart IS the receipt.
const UI_TOOL_NAMES = new Set([
  'ask_user_choice', 'request_file', 'show_checklist', 'offer_samples',
  'connect_source', 'show_page',
]);
const PRESENTATION_TOOLS = new Set([
  ...UI_TOOL_NAMES, 'render_chart', 'render_table',
]);

// Only genuine data/platform calls count as the audit trail.
const querySourcesOf = (message) => (message?.sources || [])
  .filter((s) => s && !PRESENTATION_TOOLS.has(s.tool));

// Expanded detail cards for the audit trail (opened from the actions row).
const SourceDetails = ({ sources }) => (
  <Stack spacing={1} sx={{ mt: 1.25 }}>
    {sources.map((s, i) => (
      <Box
        key={i}
        sx={(t) => {
          const u = surfacesFor(t);
          return {
            p: 1, borderRadius: '8px',
            bgcolor: u.panel,
            border: `1px solid ${u.border}`,
            fontFamily: 'monospace', fontSize: 11,
            color: u.muted,
          };
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          {s.ok ? (
            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />
          ) : (
            <ErrorOutlineIcon sx={{ fontSize: 14, color: 'error.main' }} />
          )}
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'inherit' }}>
            {s.tool || 'run_sql'}
          </Typography>
          {s.row_count != null && (
            <Chip size="small" label={`${s.row_count} rows`} sx={{ height: 18, fontSize: 10 }} />
          )}
        </Stack>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(s.spec || {}, null, 2)}
        </pre>
        {s.error && (
          <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
            {s.error}
          </Typography>
        )}
      </Box>
    ))}
  </Stack>
);


// ---------------------------------------------------------------------------
// Shimmer system (Gemini-style loading language).
//
// One shared sweep: a soft teal->lime shine travelling across ghost
// shapes. Used in three places:
//   * ShimmerLines  - "thinking" placeholder before the first event
//   * ShimmerTail   - the line currently being written under streaming
//                     text (appears, fills, becomes real text)
//   * Chart/TableSkeleton - ghost panels while render_chart /
//                     render_table are running
// ---------------------------------------------------------------------------

const shimmerSweep = keyframes`
  0%   { background-position: -460px 0; }
  100% { background-position: calc(460px + 100%) 0; }
`;

// Blocks (skeletons, panels, pills) ease in instead of popping so the
// stream reads as one continuous flow.
const blockIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: none; }
`;
const enterSx = {
  animation: `${blockIn} 260ms cubic-bezier(0.2, 0.7, 0.3, 1) both`,
};

// A single ghost bar. The travelling shine is mostly a neutral sheen
// with only a whisper of brand teal/lime - calm, not candy.
const shimmerBarSx = (theme, height = 16) => {
  const dark = theme.palette.mode === 'dark';
  const base  = dark ? 'rgba(233,237,231,0.055)' : 'rgba(40,50,35,0.065)';
  const sheen = dark ? 'rgba(233,237,231,0.09)'  : 'rgba(40,50,35,0.05)';
  const teal  = dark ? 'rgba(94,230,208,0.10)'   : 'rgba(25,117,99,0.09)';
  const lime  = dark ? 'rgba(192,251,110,0.11)'  : 'rgba(108,179,31,0.09)';
  return {
    height,
    borderRadius: `${Math.ceil(height / 2)}px`,
    backgroundColor: base,
    backgroundImage: `linear-gradient(100deg, transparent 20%, ${sheen} 36%, ${teal} 46%, ${lime} 54%, ${sheen} 64%, transparent 80%)`,
    backgroundSize: '460px 100%',
    backgroundRepeat: 'no-repeat',
    animation: `${shimmerSweep} 2.1s cubic-bezier(0.45, 0, 0.25, 1) infinite`,
  };
};

// "Thinking": three organic lines fading down the stack.
const ShimmerLines = () => (
  <Stack
    spacing={1.3}
    role="status"
    aria-label="Thinking"
    sx={{ maxWidth: 480, pt: 0.75, pb: 0.5, ...enterSx }}
  >
    {[{ w: 94, o: 1 }, { w: 86, o: 0.8 }, { w: 58, o: 0.6 }].map(({ w, o }, i) => (
      <Box
        key={i}
        sx={(theme) => ({
          ...shimmerBarSx(theme),
          width: `${w}%`,
          opacity: o,
          animationDelay: `${i * 160}ms`,
        })}
      />
    ))}
  </Stack>
);

// "Writing": the not-yet-finished line(s) under live streamed text.
// Replaces the old blinking caret - as each line completes it turns
// into real text and the shimmer moves down.
const ShimmerTail = () => (
  <Stack
    spacing={1.1}
    role="status"
    aria-label="Writing"
    sx={{ maxWidth: 480, mt: 1.25, mb: 0.25, ...enterSx }}
  >
    <Box sx={(theme) => ({ ...shimmerBarSx(theme, 15), width: '68%' })} />
    <Box
      sx={(theme) => ({
        ...shimmerBarSx(theme, 15),
        width: '38%',
        opacity: 0.65,
        animationDelay: '180ms',
      })}
    />
  </Stack>
);

// The sketch's "panel card" wrapper - shared by real chart/table
// attachments and their loading skeletons so the ghost sits exactly
// where the real thing will land.
const panelCardSx = (t) => {
  const s = surfacesFor(t);
  return {
    my: 1, p: '16px 18px 12px',
    borderRadius: '18px',
    border: `1px solid ${s.border}`,
    background: t.palette.mode === 'dark'
      ? `linear-gradient(180deg, ${s.panel2}, ${s.panel})`
      : s.panel,
    boxShadow: t.palette.mode === 'dark' ? 'none' : '0 2px 12px rgba(40,50,35,0.06)',
    ...enterSx,
  };
};

// Ghost line chart: faint grid, two self-drawing paths, sweeping shine.
const chartDraw = keyframes`
  0%        { stroke-dashoffset: 1500; opacity: 0.9; }
  70%       { stroke-dashoffset: 0;    opacity: 0.9; }
  88%       { opacity: 0.25; }
  100%      { stroke-dashoffset: 0;    opacity: 0; }
`;

const ChartSkeleton = () => (
  <Box sx={panelCardSx} role="status" aria-label="Drawing a chart">
    <Box sx={(theme) => ({ ...shimmerBarSx(theme, 14), width: '34%', mb: 2 })} />
    <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Box
          key={i}
          sx={(t) => ({
            position: 'absolute', left: 0, right: 0,
            top: `${8 + i * 22}%`, height: '1px',
            bgcolor: surfacesFor(t).border,
          })}
        />
      ))}
      <Box
        component="svg"
        viewBox="0 0 600 200"
        preserveAspectRatio="none"
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      >
        <Box
          component="path"
          d="M0,158 C50,130 85,166 140,140 C200,112 235,148 300,102 C360,62 400,118 460,80 C515,46 560,88 600,58"
          sx={(t) => ({
            fill: 'none',
            stroke: t.palette.mode === 'dark' ? 'rgba(94,230,208,0.30)' : 'rgba(25,117,99,0.28)',
            strokeWidth: 2.5,
            strokeLinecap: 'round',
            strokeDasharray: 1500,
            animation: `${chartDraw} 3s ease-in-out infinite`,
          })}
        />
        <Box
          component="path"
          d="M0,182 C60,170 100,178 160,164 C230,148 270,160 330,138 C400,112 440,130 500,108 C545,92 575,102 600,92"
          sx={(t) => ({
            fill: 'none',
            stroke: t.palette.mode === 'dark' ? 'rgba(192,251,110,0.26)' : 'rgba(108,179,31,0.26)',
            strokeWidth: 2.5,
            strokeLinecap: 'round',
            strokeDasharray: 1500,
            animation: `${chartDraw} 3s ease-in-out infinite`,
            animationDelay: '350ms',
          })}
        />
      </Box>
    </Box>
    <Stack direction="row" spacing={1.25} sx={{ mt: 1.75 }}>
      <Box sx={(theme) => ({ ...shimmerBarSx(theme, 10), width: 64 })} />
      <Box sx={(theme) => ({ ...shimmerBarSx(theme, 10), width: 52, animationDelay: '200ms' })} />
    </Stack>
  </Box>
);

// Ghost table: header row + staggered data rows.
const TableSkeleton = () => {
  const headerWidths = ['22%', '30%', '22%', '14%'];
  const rows = [
    ['20%', '28%', '18%', '12%'],
    ['16%', '32%', '22%', '10%'],
    ['22%', '26%', '16%', '14%'],
    ['18%', '30%', '20%', '12%'],
  ];
  return (
    <Box sx={panelCardSx} role="status" aria-label="Building a table">
      <Box sx={(theme) => ({ ...shimmerBarSx(theme, 14), width: '30%', mb: 2 })} />
      <Stack direction="row" spacing={2} sx={{ pb: 1.25 }}>
        {headerWidths.map((w, i) => (
          <Box key={i} sx={(theme) => ({ ...shimmerBarSx(theme, 10), width: w, opacity: 0.55 })} />
        ))}
      </Stack>
      <Box sx={(t) => ({ borderTop: `1px solid ${surfacesFor(t).border}` })} />
      <Stack spacing={1.5} sx={{ pt: 1.5, pb: 0.5 }}>
        {rows.map((widths, r) => (
          <Stack key={r} direction="row" spacing={2}>
            {widths.map((w, c) => (
              <Box
                key={c}
                sx={(theme) => ({
                  ...shimmerBarSx(theme, 13),
                  width: w,
                  animationDelay: `${r * 140}ms`,
                })}
              />
            ))}
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

// Ghost editor while the model writes + runs a program: dark code
// surface with staggered shimmering "lines of code".
const CodeSkeleton = () => {
  const lineWidths = ['46%', '72%', '58%', '30%', '64%', '52%', '38%'];
  return (
    <Box
      role="status" aria-label="Writing code"
      sx={{
        my: 1.25, borderRadius: '14px', overflow: 'hidden',
        border: '1px solid #262b26', maxWidth: 760,
      }}
    >
      <Stack
        direction="row" alignItems="center" spacing={1}
        sx={{ px: 1.5, py: 0.7, bgcolor: '#171a17', borderBottom: '1px solid #262b26' }}
      >
        <Stack direction="row" spacing={0.55}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff6057' }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#febc2e' }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#28c840' }} />
        </Stack>
        <Typography sx={{ fontFamily: 'monospace', fontSize: 10.5, color: '#96a096' }}>
          python
        </Typography>
      </Stack>
      <Stack spacing={1.1} sx={{ bgcolor: '#111411', px: 2, py: 1.75 }}>
        {lineWidths.map((w, i) => (
          <Box
            key={i}
            sx={(theme) => ({
              ...shimmerBarSx(theme, 10),
              width: w,
              ml: i === 3 || i === 5 ? 3 : 0,
              animationDelay: `${i * 110}ms`,
            })}
          />
        ))}
      </Stack>
    </Box>
  );
};

// Running render_chart / render_table / execute_code calls show a ghost
// of the thing they're producing instead of a receipt pill.
const isSkeletonTool = (b) => b?.type === 'tool'
  && b.status === 'running'
  && (b.name === 'render_chart' || b.name === 'render_table'
      || b.name === 'execute_code');


// ---------------------------------------------------------------------------
// Smooth streaming text (Gemini-style).
//
// SSE deltas land in uneven bursts; rendering them raw makes the text
// jump forward in lumps. Instead we keep the full text as the target
// and REVEAL it with a rAF loop: the reveal speed scales with the
// backlog, so it types smoothly when the model is steady and catches
// up quickly after a burst - never lagging more than a beat behind.
// ---------------------------------------------------------------------------

const useSmoothText = (text, live) => {
  const full = text || '';
  // History / completed messages mount already caught up.
  const [shownLen, setShownLen] = useState(live ? 0 : full.length);
  const targetRef = useRef(full);
  targetRef.current = full;
  const behind = shownLen < full.length;

  useEffect(() => {
    if (!behind) return undefined;
    if (!live) {
      // Streaming is over (or this block stopped being the live tail):
      // don't depend on rAF - snap to the full text. rAF stalls in
      // background tabs, which would otherwise freeze the reveal.
      setShownLen(targetRef.current.length);
      return undefined;
    }
    let raf;
    let last = 0;
    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      if (now - last < 33) return; // ~30fps is plenty for text
      last = now;
      setShownLen((len) => {
        const target = targetRef.current.length;
        if (len >= target) return len;
        return len + Math.max(2, Math.round((target - len) / 8));
      });
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [behind, live]);

  return full.slice(0, Math.min(shownLen, full.length));
};

const StreamedText = ({ text, live }) => {
  const shown = useSmoothText(text, live);
  return (
    <Box>
      <MarkdownBody>{shown}</MarkdownBody>
    </Box>
  );
};


const Block = ({ block, isLastBlock, streaming, interactive }) => {
  if (!block) return null;
  if (block.type === 'text') {
    return (
      <StreamedText
        text={block.text || ''}
        live={Boolean(streaming && isLastBlock)}
      />
    );
  }
  if (block.type === 'tool') {
    if (isSkeletonTool(block)) {
      if (block.name === 'execute_code') return <CodeSkeleton />;
      return block.name === 'render_chart' ? <ChartSkeleton /> : <TableSkeleton />;
    }
    return <ToolStep block={block} />;
  }
  if (block.type === 'attachment') {
    // Interactive UI blocks (options, file drops, sample pickers,
    // connector forms, checklists) render themselves - only the newest
    // assistant turn gets a live `interactive` context.
    if (INTERACTIVE_KINDS.has(block.kind)) {
      return (
        <InteractiveBlock
          kind={block.kind}
          spec={block.spec || {}}
          interactive={interactive}
        />
      );
    }
    // Platform artifacts created by tool calls (goals, alerts,
    // segments, pipelines, deployments) render as deep-link cards.
    if (block.kind === 'artifact') {
      return <ArtifactCard spec={block.spec || {}} />;
    }
    // Sandboxed code runs: the program + its output, Cursor-style.
    if (block.kind === 'code_run') {
      return <CodeRunBlock spec={block.spec || {}} />;
    }
    // show_page waypoints render NOTHING in the thread: the sidebar
    // hint bubble (driven off this same attachment via redux) is the
    // one and only surface for "this page exists".
    if (block.kind === 'page_tour') return null;
    // Charts and tables sit in the sketch's "panel card": 18px radius,
    // panel-2 -> panel gradient, quiet border.
    const inner = block.kind === 'chart' && block.spec
      ? <AskDataChart chart={block.spec} />
      : block.kind === 'table' && block.spec
        ? <AskDataTable table={block.spec} />
        : null;
    if (!inner) return null;
    return (
      <Box sx={panelCardSx}>
        {inner}
      </Box>
    );
  }
  if (block.type === 'warning') {
    // Model cold-start progress notices are informational, not alarming —
    // render them as a calm info pill with a spinner while the warm-up is
    // still the latest thing happening in the stream.
    const isInfo = block.kind === 'model_warming' || block.kind === 'model_ready';
    const showSpinner = block.kind === 'model_warming' && streaming && isLastBlock;
    return (
      <Box
        sx={(t) => ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          my: 0.5,
          px: 1.25, py: 0.4,
          borderRadius: 999,
          border: 1,
          ...enterSx,
          borderColor: isInfo ? t.palette.info.main : t.palette.warning.main,
          bgcolor: (theme) => {
            if (isInfo) {
              return theme.palette.mode === 'dark'
                ? 'rgba(80,150,255,0.08)'
                : 'rgba(80,150,255,0.10)';
            }
            return theme.palette.mode === 'dark'
              ? 'rgba(255,170,0,0.08)'
              : 'rgba(255,170,0,0.10)';
          },
          color: isInfo ? 'info.main' : 'warning.main',
          fontSize: 11.5,
        })}
      >
        {showSpinner && <CircularProgress size={11} color="inherit" />}
        <Typography variant="caption" sx={{ fontSize: 11.5, color: 'inherit' }}>
          {block.message || 'Warning'}
        </Typography>
      </Box>
    );
  }
  return null;
};


// Consecutive tool blocks render side by side as a receipts row
// (like the sketch's pill strip) instead of stacking vertically.
// Running chart/table renders break out as full-width skeletons.
const groupBlocks = (blocks) => {
  const groups = [];
  blocks.forEach((b) => {
    const last = groups[groups.length - 1];
    if (b?.type === 'tool' && !isSkeletonTool(b) && last?.kind === 'tools') {
      last.items.push(b);
    } else if (b?.type === 'tool' && !isSkeletonTool(b)) {
      groups.push({ kind: 'tools', items: [b] });
    } else {
      groups.push({ kind: 'single', block: b });
    }
  });
  return groups;
};


const AssistantBody = ({ message, interactive }) => {
  const { envelope, pending, streaming, content } = message || {};
  const rawBlocks = Array.isArray(message?.blocks) ? message.blocks : [];
  // A finished UI tool renders its interactive block right below - the
  // receipt pill would just repeat it. Keep the pill while running (it is
  // the shimmer feedback) and on error (so failures stay visible).
  const blocks = rawBlocks.filter((b) => !(
    b?.type === 'tool'
    && UI_TOOL_NAMES.has(b.name)
    && b.status !== 'running'
    && b.status !== 'error'
  ));

  // Streaming OR completed turns both render through the block list
  // so order is fully preserved (text -> tool -> text -> attachment
  // -> ...). When streaming and we have no blocks yet, show a
  // shimmer skeleton until the first delta arrives.
  if (streaming && blocks.length === 0) {
    return <ShimmerLines />;
  }

  if (blocks.length > 0) {
    const groups = groupBlocks(blocks);
    // While streaming, a shimmer "line being written" follows the tail
    // of the message - unless a running tool is already showing its own
    // progress (pulsing pill or chart/table skeleton).
    const last = blocks[blocks.length - 1];
    const lastToolRunning = last?.type === 'tool' && last.status === 'running';
    const showTail = streaming && !lastToolRunning;
    return (
      <Stack spacing={1}>
        {groups.map((g, gi) => {
          if (g.kind === 'tools') {
            return (
              <Box key={gi} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, my: 0.25, ...enterSx }}>
                {g.items.map((b, i) => <ToolStep key={i} block={b} />)}
              </Box>
            );
          }
          const isLast = gi === groups.length - 1;
          return (
            <Block
              key={gi} block={g.block}
              streaming={streaming}
              isLastBlock={isLast}
              interactive={interactive}
            />
          );
        })}
        {showTail && <ShimmerTail />}
      </Stack>
    );
  }

  // Defensive fallback for messages with no blocks AND no streaming
  // state (e.g. a malformed cache entry). Render the legacy envelope
  // shape so we still show *something*.
  if (pending) {
    return <ShimmerLines />;
  }
  if (!envelope) return <Typography variant="body2">(no answer)</Typography>;
  const text = envelope.text || content || '';
  return (
    <Stack spacing={1.25}>
      {text && <MarkdownBody>{text}</MarkdownBody>}
      {envelope.type === 'table' && envelope.table && <AskDataTable table={envelope.table} />}
      {envelope.type === 'chart' && envelope.chart && <AskDataChart chart={envelope.chart} />}
    </Stack>
  );
};


// Quiet ghost-pill action used under completed answers (sketch .ghost).
const GhostAction = ({ icon: Icon, label, onClick }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    sx={(theme) => {
      const s = surfacesFor(theme);
      return {
        display: 'inline-flex', alignItems: 'center', gap: 0.85,
        px: '13px', py: '6px',
        borderRadius: 999,
        border: `1px solid ${s.border2}`,
        color: s.muted,
        fontSize: 12,
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'all 120ms',
        '&:hover': {
          color: s.text,
          bgcolor: s.panel,
        },
      };
    }}
  >
    <Icon sx={{ fontSize: 12.5 }} />
    {label}
  </Box>
);


// Collect the assistant's prose so Copy grabs something useful.
const textOf = (message) => {
  const blocks = Array.isArray(message?.blocks) ? message.blocks : [];
  const fromBlocks = blocks
    .filter((b) => b?.type === 'text' && b.text)
    .map((b) => b.text)
    .join('\n\n');
  return fromBlocks || message?.envelope?.text || message?.content || '';
};


const AssistantActions = ({ message, onRetry, retryQuestion }) => {
  const [copied, setCopied] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const usage = message.usage;
  const querySources = querySourcesOf(message);

  const handleCopy = () => {
    const text = textOf(message);
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }).catch(() => {});
  };

  // "2 queries" when everything was run_sql, "2 tool calls" for a mix of
  // platform actions - both open the same audit-trail details.
  const allSql = querySources.every((s) => (s.tool || 'run_sql') === 'run_sql');
  const noun = allSql
    ? (querySources.length === 1 ? 'query' : 'queries')
    : (querySources.length === 1 ? 'tool call' : 'tool calls');

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
        <GhostAction
          icon={copied ? CheckIcon : ContentCopyIcon}
          label={copied ? 'Copied' : 'Copy'}
          onClick={handleCopy}
        />
        {onRetry && retryQuestion && (
          <GhostAction
            icon={ReplayIcon}
            label="Try again"
            onClick={() => onRetry(retryQuestion)}
          />
        )}
        {querySources.length > 0 && (
          <GhostAction
            icon={StorageIcon}
            label={`${querySources.length} ${noun}${showSources ? ' ▴' : ''}`}
            onClick={() => setShowSources((v) => !v)}
          />
        )}
        {usage && (
          <Tooltip
            arrow
            title={`in ${usage.input_tokens || 0} tok · out ${usage.output_tokens || 0} tok` +
              (usage.tool_calls ? ` · ${usage.tool_calls} ${usage.tool_calls === 1 ? 'call' : 'calls'}` : '')}
          >
            <Typography
              sx={(t) => ({ pl: 0.5, fontSize: 10.5, color: surfacesFor(t).faint })}
            >
              {formatCredits(usage.cost_credits)} spent
            </Typography>
          </Tooltip>
        )}
      </Stack>
      {showSources && querySources.length > 0 && (
        <SourceDetails sources={querySources} />
      )}
    </>
  );
};


const AskDataMessage = ({ message, userName, onRetry, retryQuestion, interactive }) => {
  const isUser = message.role === 'user';

  // App-initiated turns (e.g. the setup kickoff instruction) are context
  // for the model, not conversation - never render the bubble.
  if (isUser && message.hidden) return null;

  if (isUser) {
    return (
      <Stack
        direction="row" spacing={1.25}
        justifyContent="flex-end" alignItems="flex-start"
        sx={{ mt: 3.25, mb: 2.75 }}
      >
        <Box
          sx={(theme) => {
            const s = surfacesFor(theme);
            return {
              maxWidth: '70%',
              px: 2, py: '11px',
              borderRadius: '16px 16px 4px 16px',
              border: `1px solid ${s.border}`,
              bgcolor: s.panel2,
              boxShadow: theme.palette.mode === 'dark'
                ? 'none'
                : '0 2px 10px rgba(40,50,35,0.07)',
            };
          }}
        >
          <UserAttachments
            attachments={message.attachments}
            hasText={Boolean(message.content)}
          />
          <Typography
            sx={(t) => ({
              whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.5,
              color: surfacesFor(t).text,
            })}
          >
            {message.content}
          </Typography>
        </Box>
        {/* Same avatar treatment as the global header. */}
        <Avatar
          sx={{
            mt: 0.25,
            width: 26, height: 26,
            fontSize: 10, fontWeight: 700,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          {initialsOf(userName)}
        </Avatar>
      </Stack>
    );
  }

  return (
    <Box sx={{ mb: 3.5 }}>
      <AssistantBody message={message} interactive={interactive} />
      {!message.pending && !message.streaming && (
        <AssistantActions
          message={message}
          onRetry={onRetry}
          retryQuestion={retryQuestion}
        />
      )}
    </Box>
  );
};

export default AskDataMessage;
